import { useCallback, useState } from 'react';

import { isSessionExpiredModalOpen } from '@/contexts/SessionExpired/utils';

type Callback<D, P extends unknown[] = []> = (...args: P) => Promise<D>;
export type HookReturn<D, P extends unknown[] = []> = {
  data: Nullable<D>;
  error: Nullable<unknown>;
  isLoading: boolean;
  hasBeenFetched: boolean;
  load: (...args: P) => Promise<void>;
  change: (input: Nullable<D>) => void;
  reset: () => void;
};

type Options<D> = {
  mutate?: (data: D) => Promise<D>;
  defaultDataValue?: D;
};

export const useAsyncDataLoader = <D, P extends unknown[] = []>(
  cb: Callback<D, P>,
  options?: Options<D>,
): HookReturn<D, P> => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasBeenFetched, setHasBeenFetched] = useState<boolean>(false);
  const [data, setData] = useState<Nullable<D>>(options?.defaultDataValue || null);
  const [error, setError] = useState<Nullable<unknown>>(null);

  const load = useCallback(
    async (...args: P) => {
      if (isSessionExpiredModalOpen()) return;
      try {
        setLoading(true);
        setData(await (options?.mutate || ((i: D): D => i))(await cb(...args)));
      } catch (err) {
        console.error(err);
        if (!isSessionExpiredModalOpen()) {
          setData(options?.defaultDataValue || null);
        }
        setError(err);
      } finally {
        setHasBeenFetched(true);
        setLoading(false);
      }
    },
    [cb, options?.mutate],
  );

  const reset = useCallback(() => {
    setData(options?.defaultDataValue || null);
    setError(null);
    setHasBeenFetched(false);
    setLoading(false);
  }, [options?.defaultDataValue]);

  return {
    isLoading,
    hasBeenFetched,
    error,
    data,
    load,
    change: setData,
    reset,
  };
};
